import React, { useEffect, useState } from 'react';
import { Loader2, Heart, Trash2, ChevronRight, Star, ShieldCheck } from 'lucide-react';
import { favoriteService } from '../../services/favoriteService';
import { fetchTeamInfo } from '../../services/footballService';
import { getTeamName } from '../../services/teamService';
import { Language } from '../../types';
import { t } from '../../i18n';
import { ConfirmModal } from '../Common/ConfirmModal';

interface FavoriteTeamsViewProps {
    userId: string;
    lang: Language;
    onSelectionChange?: (teamId: string | null) => void;
    onSaveTrigger?: (teamId: string) => Promise<void>;
}

export const FavoriteTeamsView: React.FC<FavoriteTeamsViewProps> = ({ 
    userId, 
    lang, 
    onSelectionChange,
    onSaveTrigger 
}) => {
    const [favorites, setFavorites] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [defaultTeamId, setDefaultTeamId] = useState<string | null>(null);
    const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean, teamId: string | null }>({
        isOpen: false,
        teamId: null
    });

    const loadFavorites = async () => {
        setLoading(true);
        try {
            const selections = await favoriteService.getFavoriteTeams();
            const teamInfos = selections.map((s) => ({
                team: {
                    id: s.team_id,
                    name: `Team ${s.team_id}`,
                    logo: `https://media.api-sports.io/football/teams/${s.team_id}.png`
                },
                is_favorite: s.is_favorite
            }));
            
            const defaultTeam = teamInfos.find((f: any) => f.is_favorite);
            if (defaultTeam) {
                setDefaultTeamId(defaultTeam.team.id.toString());
            }

            // Sort to put is_favorite: true first
            const sorted = teamInfos.sort((a: any, b: any) => {
                if (a.is_favorite === b.is_favorite) return 0;
                return a.is_favorite ? -1 : 1;
            });
            setFavorites(sorted);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFavorites();
    }, [userId]);

    const handleRemoveRequest = (e: React.MouseEvent, teamId: string) => {
        e.stopPropagation();
        setConfirmModal({ isOpen: true, teamId });
    };

    const handleConfirmRemove = async () => {
        if (!confirmModal.teamId) return;
        const success = await favoriteService.removeFavorite(confirmModal.teamId);
        if (success) {
            setFavorites(prev => prev.filter(f => f.team.id.toString() !== confirmModal.teamId));
            if (defaultTeamId === confirmModal.teamId) {
                setDefaultTeamId(null);
            }
        }
        setConfirmModal({ isOpen: false, teamId: null });
    };

    const handleSetDefault = (teamId: string) => {
        setDefaultTeamId(teamId);
        if (onSelectionChange) {
            onSelectionChange(teamId);
        }
    };

    useEffect(() => {
        if (onSaveTrigger) {
            // This is a pattern to allow parent to trigger internal loadFavorites
            (window as any).refreshFavoriteTeams = loadFavorites;
        }
        return () => {
            delete (window as any).refreshFavoriteTeams;
        };
    }, [onSaveTrigger]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 className="animate-spin text-rose-500" />
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse">
                    {t(lang, 'syncingMatchIntelDesc')}
                </span>
            </div>
        );
    }

    if (favorites.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-32 px-6 text-center gap-4">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-200">
                    <Heart size={32} />
                </div>
                <div>
                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-1">
                        {t(lang, 'noFavoriteTeams')}
                    </h3>
                    <p className="text-[11px] font-medium text-slate-400 leading-relaxed">
                        {t(lang, 'noFavoriteTeamsDesc')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="p-6 pb-12 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-x-6 gap-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                {favorites.map((f) => {
                    const isSelectedAsDefault = defaultTeamId === f.team.id.toString();
                    return (
                        <div 
                            key={f.team.id}
                            className="flex flex-col items-center"
                        >
                            <div 
                                onClick={() => handleSetDefault(f.team.id.toString())}
                                className={`relative w-20 h-20 bg-slate-50 rounded-[2.5rem] flex items-center justify-center border-2 transition-all duration-300 cursor-pointer group active:scale-95 ${
                                    isSelectedAsDefault 
                                    ? 'border-amber-400 bg-white shadow-xl shadow-amber-100 scale-110' 
                                    : 'border-slate-50 md:hover:border-slate-200 shadow-sm'
                                }`}
                            >
                                <img src={f.team.logo} alt={f.team.name} className="w-12 h-12 object-contain" />
                                
                                <button
                                    onClick={(e) => handleRemoveRequest(e, f.team.id.toString())}
                                    className="absolute -top-1 -right-1 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-lg border border-rose-50 group-hover:scale-110 transition-transform z-10"
                                >
                                    <Heart size={14} className="text-rose-500 fill-current" />
                                </button>

                                {isSelectedAsDefault && (
                                    <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center shadow-lg border-2 border-white z-10 animate-in zoom-in duration-300">
                                        <Star size={12} fill="currentColor" />
                                    </div>
                                )}
                            </div>
                            <div className="mt-4 flex flex-col items-center gap-1 w-full">
                                <span className={`text-[10px] font-black uppercase tracking-tight text-center w-full px-1 transition-colors line-clamp-2 h-6 leading-[1.2] ${
                                    isSelectedAsDefault ? 'text-amber-600' : 'text-slate-900'
                                }`}>
                                    {getTeamName(f.team.id.toString(), lang, f.team.name)}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <ConfirmModal 
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, teamId: null })}
                onConfirm={handleConfirmRemove}
                title={t(lang, 'removeFavoriteTeam')}
                description={t(lang, 'removeFavoriteTeamConfirm')}
                confirmLabel={t(lang, 'delete')}
                cancelLabel={t(lang, 'cancel')}
            />
        </>
    );
};
